import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "All Works" },
  { key: "original", label: "Originals" },
  { key: "print", label: "Prints" },
];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const { add } = useCart();
  const { settings, loaded: settingsLoaded } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (settingsLoaded && !settings.shop_enabled) {
      navigate("/portfolio", { replace: true });
    }
  }, [settingsLoaded, settings.shop_enabled, navigate]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (tab === "all" ? products : products.filter((p) => p.kind === tab)),
    [products, tab]
  );

  const onAdd = (p) => {
    add(p);
    toast.success(`${p.title} added to cart`);
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-16 md:py-24" data-testid="page-shop">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
        <div>
          <div className="label-eyebrow mb-3">The Shop</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A1F1B]">
            Originals & prints.
          </h1>
          <p className="mt-4 max-w-xl text-[#4E5C50]">
            Each original is a one-of-a-kind layered work. Prints are
            archival giclée editions, signed and numbered by the artist.
          </p>
        </div>
        <div className="flex gap-2" data-testid="shop-tabs">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                tab === t.key
                  ? "bg-[#3B4A3F] text-[#F7F6F2] border-[#3B4A3F]"
                  : "border-[#D8D3CC] text-[#4E5C50] hover:border-[#3B4A3F]"
              }`}
              data-testid={`shop-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-32 text-center text-[#849383]" data-testid="shop-loading">
          Loading shop…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-32 text-center text-[#849383]" data-testid="shop-empty">
          No items in this category yet.
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          data-testid="shop-grid"
        >
          {filtered.map((p) => {
            const isSoldOriginal = p.kind === "original" && !p.in_stock;
            return (
            <article
              key={p.id}
              className="border border-[#D8D3CC] p-5 flex flex-col bg-[#F7F6F2] art-card"
              data-testid={`shop-product-${p.id}`}
            >
              <div className="relative overflow-hidden bg-[#EFECE5]">
                <img
                  src={imgUrl(p.image_url)}
                  alt={p.title}
                  loading="lazy"
                  decoding="async"
                  className={`w-full aspect-[4/5] object-contain ${isSoldOriginal ? "opacity-70" : ""}`}
                />
                {isSoldOriginal && (
                  <div
                    className="absolute top-3 left-3 flex items-center gap-2 bg-[#F7F6F2]/95 backdrop-blur-sm border border-[#3B4A3F] px-3 py-1.5"
                    data-testid={`shop-sold-badge-${p.id}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A39171]" />
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#3B4A3F] font-medium">
                      Sold
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow">{p.kind}</div>
                  <h3 className="font-serif-display text-2xl text-[#1A1F1B] mt-1 leading-tight">
                    {p.title}
                  </h3>
                  <div className="text-xs text-[#849383] mt-2">
                    {p.medium}
                    {p.dimensions && <> · {p.dimensions}</>}
                  </div>
                </div>
                <div className="font-serif-display text-2xl text-[#3B4A3F] whitespace-nowrap">
                  ${p.price.toFixed(0)}
                </div>
              </div>
              {p.description && (
                <p className="mt-3 text-sm text-[#4E5C50] leading-relaxed">
                  {p.description}
                </p>
              )}
              {isSoldOriginal ? (
                <a
                  href="/commissions"
                  className="btn-secondary mt-6 w-full text-center inline-block"
                  data-testid={`shop-sold-cta-${p.id}`}
                >
                  Commission a similar piece
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => onAdd(p)}
                  disabled={!p.in_stock}
                  className="btn-primary mt-6 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`shop-add-${p.id}`}
                >
                  {p.in_stock ? "Add to Cart" : "Sold"}
                </button>
              )}
            </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Shop;
